import React from 'react';
import packageJson from '../package.json';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VERSION = `v${packageJson.version}`;

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b-4 border-indigo-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-black text-indigo-600">📖 使用說明</h2>
            <span className="text-sm font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{VERSION}</span>
          </div>
          <button 
            onClick={onClose}
            className="text-3xl text-slate-400 hover:text-slate-600 transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 space-y-6 text-slate-800">
          <section>
            <h3 className="text-2xl font-black text-indigo-600 mb-3">🎯 快速開始</h3>
            <ol className="space-y-2 list-decimal list-inside text-lg">
              <li>點擊「選取照片」選擇您想要使用的圖片</li>
              <li>選擇適合的圖片比例（1:1、4:3、16:9 等）</li>
              <li>選擇或輸入祝賀語</li>
              <li>調整顏色、字體和樣式</li>
              <li>點擊「儲存圖片」完成製作</li>
            </ol>
          </section>

          <section>
            <h3 className="text-2xl font-black text-indigo-600 mb-3">✏️ 文字移動</h3>
            <p className="text-lg mb-2">
              在預覽區<strong>直接點擊並拖動</strong>文字即可調整位置：
            </p>
            <ul className="space-y-2 list-disc list-inside text-lg ml-4">
              <li>點擊祝賀語或日期文字開始移動</li>
              <li>移動時會顯示「📍 移動中...」提示</li>
              <li>拖動手指調整到想要的位置</li>
              <li>鬆開手指完成位置調整</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-black text-indigo-600 mb-3">🎨 顏色選擇</h3>
            <p className="text-lg mb-2">
              有兩種方式選擇顏色：
            </p>
            <ul className="space-y-2 list-disc list-inside text-lg ml-4">
              <li><strong>預設顏色：</strong>點擊顏色方塊快速選擇（包含彩虹色）</li>
              <li><strong>自訂顏色：</strong>點擊🎨圖示直接開啟顏色選擇器</li>
              <li>支援祝賀語、日期、邊框的自訂顏色選擇</li>
              <li>手機和電腦版都可以正常使用顏色選擇器</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-black text-indigo-600 mb-3">📐 進階功能</h3>
            <ul className="space-y-2 list-disc list-inside text-lg ml-4">
              <li><strong>照片微調：</strong>在「進階照片縮放與位移」中調整縮放、左右上下移動</li>
              <li><strong>邊框設定：</strong>選擇邊框樣式（無/白/黑/自訂）、粗細和顯示邊（上下左右）</li>
              <li><strong>進階文字設定：</strong>調整祝賀語大小、描邊、字距，以及陰影、發光、垂直排列等效果</li>
              <li><strong>進階日期設定：</strong>可單獨設定日期的顏色、大小、描邊和字距，與祝賀語分開調整</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xl font-black text-indigo-600 mb-3">💾 儲存與分享</h3>
            <ul className="space-y-2 list-disc list-inside text-lg ml-4">
              <li>點擊「💾 儲存圖片」按鈕生成最終圖片</li>
              <li><strong>手機版：</strong>
                <ul className="ml-6 mt-1 space-y-1 list-disc">
                  <li>點擊「📤 分享圖片 / 儲存」按鈕，可分享到其他App或儲存到相簿</li>
                  <li>或直接長按預覽圖片，選擇「儲存圖片」</li>
                </ul>
              </li>
              <li><strong>電腦版：</strong>點擊「💾 下載圖片」按鈕直接下載到電腦</li>
            </ul>
          </section>

          <section className="bg-indigo-50 p-4 rounded-2xl border-2 border-indigo-200">
            <h3 className="text-xl font-black text-indigo-700 mb-2">💡 小提示</h3>
            <ul className="space-y-1 text-lg text-indigo-800">
              <li>• 建議使用高解析度圖片以獲得最佳效果</li>
              <li>• 文字位置可以隨時調整，不用擔心</li>
              <li>• 進階設定可以讓您的早安圖更獨特</li>
            </ul>
          </section>
        </div>

        <div className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-200 px-6 py-4">
          <button 
            onClick={onClose}
            className="w-full bg-indigo-600 text-white py-4 rounded-xl text-xl font-black shadow-lg active:scale-95 transition-transform"
          >
            我知道了
          </button>
        </div>
      </div>
    </div>
  );
};
